import { useCallback, useState, useEffect, useMemo } from 'react';
import { useGameStore } from '@hollowveil/shared';
import type { SubQuest, QuestObjective } from '@hollowveil/shared';
import './QuestUI.css';

// Check if we're on mobile
const isMobile = () => window.innerWidth <= 600;

// Filter objectives based on visibility conditions
function filterVisibleObjectives(
  objectives: QuestObjective[],
  solvedRiddles: Set<string>,
  completedRooms: string[]
): QuestObjective[] {
  return objectives.filter(obj => {
    // Check hideAfterRiddleSolved - if riddle is solved, hide this objective
    if (obj.hideAfterRiddleSolved && solvedRiddles.has(obj.hideAfterRiddleSolved)) {
      return false;
    }

    // Check hideAfterRoomComplete - if room is complete, hide this objective
    if (obj.hideAfterRoomComplete && completedRooms.includes(obj.hideAfterRoomComplete)) {
      return false;
    }

    // Check showAfterRiddleSolved - only show if riddle is solved
    if (obj.showAfterRiddleSolved && !solvedRiddles.has(obj.showAfterRiddleSolved)) {
      return false;
    }

    // Check showAfterRoomComplete - only show if room is complete
    if (obj.showAfterRoomComplete && !completedRooms.includes(obj.showAfterRoomComplete)) {
      return false;
    }

    return true;
  });
}

// Filter sub-quests based on visibility conditions
function filterVisibleSubQuests(
  subQuests: SubQuest[] | undefined,
  solvedRiddles: Set<string>
): SubQuest[] {
  if (!subQuests) return [];
  return subQuests.filter(sq => {
    // Check hideAfterRiddleSolved - if riddle is solved, hide this sub-quest
    if (sq.hideAfterRiddleSolved && solvedRiddles.has(sq.hideAfterRiddleSolved)) {
      return false;
    }

    // Check showAfterRiddleSolved - only show if riddle is solved
    if (sq.showAfterRiddleSolved && !solvedRiddles.has(sq.showAfterRiddleSolved)) {
      return false;
    }

    return true;
  });
}

interface QuestToggleButtonProps {
  isVisible: boolean;
  onToggle: () => void;
  incompleteCount: number;
}

function QuestToggleButton({ isVisible, onToggle, incompleteCount }: QuestToggleButtonProps) {
  return (
    <button
      className={`quest-toggle-btn ${incompleteCount > 0 ? 'has-objectives' : ''}`}
      onClick={onToggle}
      title={isVisible ? 'Masquer les quêtes' : 'Afficher les quêtes'}
    >
      {isVisible ? '📜' : '📜'}
      {incompleteCount > 0 && !isVisible && (
        <span className="badge">{incompleteCount}</span>
      )}
    </button>
  );
}

export function QuestUI() {
  const { currentQuest, questComplete, currentWorld, restartQuest, advanceToNextRoom, currentRoom, rooms, solvedRiddles, completedRooms } = useGameStore();

  // Quest visibility state
  const [isVisible, setIsVisible] = useState(!isMobile());
  // Collapsed state (for expanding/collapsing the quest details)
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedSubQuests, setExpandedSubQuests] = useState<Set<string>>(new Set());

  // Update visibility on resize
  useEffect(() => {
    const handleResize = () => {
      // On mobile, default to hidden
      if (isMobile() && isVisible) {
        setIsVisible(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible]);

  const handleRestart = useCallback(() => {
    restartQuest();
  }, [restartQuest]);

  const handleNextRoom = useCallback(() => {
    advanceToNextRoom();
  }, [advanceToNextRoom]);

  const toggleVisibility = useCallback(() => {
    setIsVisible((prev) => !prev);
  }, []);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const toggleSubQuest = useCallback((subQuestId: string) => {
    setExpandedSubQuests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(subQuestId)) {
        newSet.delete(subQuestId);
      } else {
        newSet.add(subQuestId);
      }
      return newSet;
    });
  }, []);

  // Filter objectives based on visibility conditions (riddles solved, rooms completed)
  const visibleObjectives = useMemo(() => {
    if (!currentQuest) return [];
    return filterVisibleObjectives(currentQuest.objectives, solvedRiddles, completedRooms);
  }, [currentQuest, solvedRiddles, completedRooms]);

  // Filter sub-quests based on visibility conditions
  const visibleSubQuests = useMemo(() => {
    if (!currentQuest) return [];
    return filterVisibleSubQuests(currentQuest.subQuests, solvedRiddles);
  }, [currentQuest, solvedRiddles]);

  // Count incomplete objectives (only visible ones, including sub-quest objectives)
  const incompleteCount = useMemo(() => {
    let count = visibleObjectives.filter(o => !o.completed).length;
    // Also count sub-quest objectives that are visible
    for (const sq of visibleSubQuests) {
      count += sq.objectives.filter(o => !o.completed).length;
    }
    return count;
  }, [visibleObjectives, visibleSubQuests]);

  // Check if there's a next room
  const roomIds = Array.from(rooms.keys());
  const currentIndex = currentRoom ? roomIds.indexOf(currentRoom.id) : -1;
  const hasNextRoom = currentIndex >= 0 && currentIndex < roomIds.length - 1;

  // Calculate sub-quest progress
  const subQuestProgress = (subQuest: SubQuest) => {
    const completed = subQuest.objectives.filter((o) => o.completed).length;
    return { completed, total: subQuest.objectives.length };
  };

  if (!currentQuest) return null;

  return (
    <>
      {/* Toggle Button */}
      <QuestToggleButton
        isVisible={isVisible}
        onToggle={toggleVisibility}
        incompleteCount={incompleteCount}
      />

      {/* Quest Panel */}
      <div className={`quest-ui ${questComplete ? 'quest-complete' : ''} ${isCollapsed ? 'collapsed' : ''} ${!isVisible ? 'hidden' : ''}`}>
        <div className="quest-header" onClick={toggleCollapse}>
          <span className="quest-icon">{questComplete ? '✨' : '📜'}</span>
          <span className="quest-name">{currentQuest.name}</span>
          <span className="collapse-icon">{isCollapsed ? '▼' : '▲'}</span>
          <button className="restart-button" onClick={(e) => { e.stopPropagation(); handleRestart(); }} title="Recommencer">
            🔄
          </button>
        </div>

        <div className="quest-objectives">
          {/* Main objectives (filtered by visibility conditions) */}
          {visibleObjectives.map((objective) => (
            <div
              key={objective.id}
              className={`quest-objective ${objective.completed ? 'completed' : ''} ${
                objective.requiredWorld === currentWorld ? 'active-world' : 'other-world'
              }`}
            >
              <span className="objective-check">
                {objective.completed ? '✓' : objective.requiredWorld === 'spectral' ? '👁' : '○'}
              </span>
              <span className="objective-text">{objective.description}</span>
              <span className="objective-world">
                {objective.requiredWorld === 'real' ? '☀️' : objective.requiredWorld === 'spectral' ? '🌙' : ''}
              </span>
            </div>
          ))}

          {/* Sub-quests */}
          {visibleSubQuests.length > 0 && (
            <div className="sub-quests-container">
              {visibleSubQuests.map((subQuest) => {
                const progress = subQuestProgress(subQuest);
                const isExpanded = expandedSubQuests.has(subQuest.id);
                const isSubQuestComplete = subQuest.objectives.every((o) => o.completed);

                return (
                  <div key={subQuest.id} className={`sub-quest ${isSubQuestComplete ? 'completed' : ''} ${subQuest.isOptional ? 'optional' : ''}`}>
                    <div className="sub-quest-header" onClick={() => toggleSubQuest(subQuest.id)}>
                      <span className="sub-quest-icon">{subQuest.icon || '🎯'}</span>
                      <span className="sub-quest-name">{subQuest.name}</span>
                      <span className="sub-quest-progress">
                        {progress.completed}/{progress.total}
                      </span>
                      <span className="sub-quest-expand">{isExpanded ? '▲' : '▼'}</span>
                    </div>

                    {isExpanded && (
                      <div className="sub-quest-objectives">
                        {subQuest.objectives.map((objective) => (
                          <div
                            key={objective.id}
                            className={`quest-objective sub-objective ${objective.completed ? 'completed' : ''}`}
                          >
                            <span className="objective-check">
                              {objective.completed ? '✓' : '○'}
                            </span>
                            <span className="objective-text">{objective.description}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {isSubQuestComplete && subQuest.reward && (
                      <div className="sub-quest-reward">
                        {subQuest.reward}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {questComplete && (
          <div className="quest-complete-section">
            <div className="quest-complete-banner">
              Quête terminée !
            </div>
            {hasNextRoom && (
              <button className="next-room-button" onClick={handleNextRoom}>
                Salle suivante →
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
